import { useState } from 'react';
import { FloatingField } from '@/components/ui/floating-field';
import * as Icons from 'lucide-react';
import { cn } from '@/lib/utils';

/** Strip to a positive decimal money/rate string (no letters, no leading minus). */
export function sanitizeMoneyInput(raw) {
  let s = String(raw ?? '');
  s = s.replace(/[eE+\-]/g, '');
  s = s.replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  if (s.startsWith('.')) s = `0${s}`;
  return s;
}

export function sanitizeDigitsInput(raw) {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Money/rate field without native spinner toggles; blocks letters and negatives.
 */
export function MoneyField({
  label,
  value,
  onChange,
  required,
  className,
  allowEmpty = true,
  ...props
}) {
  return (
    <FloatingField
      label={label}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value ?? ''}
      required={required}
      className={cn('[&_input]:[appearance:textfield]', className)}
      onChange={(e) => {
        const next = sanitizeMoneyInput(e.target.value);
        if (!allowEmpty && next === '') return;
        onChange?.({ ...e, target: { ...e.target, value: next } });
      }}
      {...props}
    />
  );
}

export function PhoneField({ label, value, onChange, required, ...props }) {
  return (
    <FloatingField
      label={label}
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      value={value ?? ''}
      required={required}
      onChange={(e) => {
        const next = sanitizeDigitsInput(e.target.value);
        onChange?.({ ...e, target: { ...e.target, value: next } });
      }}
      {...props}
    />
  );
}

export function PasswordField({
  label,
  value,
  onChange,
  required,
  id,
  className,
  ...props
}) {
  const [show, setShow] = useState(false);
  return (
    <div className={cn('relative', className)}>
      <FloatingField
        id={id}
        label={label}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required={required}
        className="[&_input]:pr-10"
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        className="absolute right-3 top-[1.65rem] text-muted-foreground hover:text-foreground z-10"
        aria-label={show ? 'Hide password' : 'Show password'}
        onClick={() => setShow((v) => !v)}
      >
        {show ? <Icons.EyeOff className="h-4 w-4" /> : <Icons.Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
